<template>
  <BackPageLayout>
    <template #headline>
      {{ t('customcharts.page.title') }}
    </template>

    <div class="space-y-5">
      <!-- Bouton de création -->
      <div class="flex justify-between items-center">
        <p class="text-sm text-gray-600">{{ t('customcharts.page.description') }}</p>
        <button @click="showBuilder = true"
          class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <span>➕</span>
          {{ t('customcharts.page.new_chart') }}
        </button>
      </div>

      <!-- Liste des graphiques sauvegardés -->
      <div v-if="savedCharts.length > 0" class="space-y-3">
        <h3 class="text-lg font-semibold text-gray-800">{{ t('customcharts.page.saved_charts') }}</h3>

        <div class="grid gap-3">
          <div v-for="chart in savedCharts" :key="chart.id"
            class="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">

            <div class="flex items-center justify-between mb-3">
              <div>
                <h4 class="font-medium text-gray-900">{{ chart.name }}</h4>
                <p class="text-xs text-gray-500">{{
                  getChartTypeLabel(chart.config.chartType) }}
                </p>
              </div>

              <div class="flex items-center gap-2">
                <button @click="editChart(chart)"
                  class="text-blue-600 hover:text-blue-800 p-2 rounded">
                  <span class="text-sm">✏️</span>
                </button>
                <button @click="deleteChart(chart.id)"
                  class="text-red-600 hover:text-red-800 p-2 rounded">
                  <span class="text-sm">🗑️</span>
                </button>
              </div>
            </div>

            <!-- Aperçu du graphique -->
            <div class="flex items-center justify-center">
              <ChartViewer :chart="chart" :height="200" :preview="false" :center-id="centerId"
                :report-id="reportId" />
            </div>

            <div class="mt-3 text-xs text-gray-500">
              {{ t('customcharts.page.created_at') }} {{ formatDate(chart.createdAt) }}
              <span v-if="chart.updatedAt !== chart.createdAt">
                • {{ t('customcharts.page.updated_at') }} {{ formatDate(chart.updatedAt) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- État vide -->
      <div v-else class="text-center py-12">
        <div class="text-6xl mb-4">📊</div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">{{ t('customcharts.page.no_charts_title') }}</h3>
        <p class="text-gray-600 mb-6">{{ t('customcharts.page.no_charts_subtitle') }}</p>
        <button @click="showBuilder = true"
          class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium">
          {{ t('customcharts.page.create_first_chart') }}
        </button>
      </div>
    </div>

    <!-- Modal de création/édition -->
    <BottomSheet v-model:is-open="showBuilder">
      <ChartBuilder :chart="editingChart" :center-id="centerId"
        :report-id="reportId" :products-report="reportInfo?.productsReport"
        @save="handleSave" @cancel="handleCancel" />
    </BottomSheet>
  </BackPageLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import BackPageLayout from '../../layouts/BackPageLayout.vue'
import ChartBuilder from './ChartBuilder.vue'
import ChartViewer from './ChartViewer.vue'
import { useCustomChartsStore } from '../../stores/customChartsStore'
import { useSalesReportsStore } from '../../stores/salesReports'
import type { CustomChart } from '../../types/customCharts'
import BottomSheet from '@/components/BottomSheet.vue'

const route = useRoute()
const customChartsStore = useCustomChartsStore()
const salesReportsStore = useSalesReportsStore()
const { t } = useI18n()

const centerId = parseInt(route.params.centerId as string)
const reportId = route.params.id as string

const showBuilder = ref(false)
const editingChart = ref<CustomChart | null>(null)

// Graphiques filtrés pour ce rapport spécifique  
const savedCharts = ref<CustomChart[]>([])

const loadSavedCharts = async () => {
  savedCharts.value = await customChartsStore.getChartsForReport(centerId, reportId)
}

// Informations du rapport pour contexte
const reportInfo = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(centerId, reportId)
})

// Gestion de la création/édition
const editChart = (chart: CustomChart) => {
  editingChart.value = { ...chart }
  showBuilder.value = true
}

const handleSave = async (chart: CustomChart) => {
  if (editingChart.value) {
    await customChartsStore.updateChart(chart)
  } else {
    await customChartsStore.addChart(chart)
  }

  showBuilder.value = false
  editingChart.value = null
  await loadSavedCharts() // Recharger la liste
}

const handleCancel = () => {
  showBuilder.value = false
  editingChart.value = null
}

const deleteChart = async (chartId: string) => {
  if (confirm(t('common.confirm_delete_chart'))) {
    await customChartsStore.deleteChart(chartId)
    await loadSavedCharts() // Recharger la liste
  }
}

// Utilitaires
const getChartTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'sales-history': t('customcharts.templates.sales_history'),
    'families-pie': t('customcharts.templates.families_pie'),
    'top-products': t('customcharts.templates.top_products'),
    'average-basket': t('customcharts.templates.average_basket')
  }
  return labels[type] || type
}

const formatDate = (date: Date | string): string => {
  const d = new Date(date)
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

onMounted(async () => {
  await loadSavedCharts()
})
</script>